import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ShieldOff, ShieldCheck, Mail, Phone, MapPin, Trash2, Plus, Save, Users } from "lucide-react";
import { toast } from "sonner";

const computeAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

export default function AdminContractorProfile() {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [draft, setDraft] = useState(null);
  const [clientsAssigned, setClientsAssigned] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/users/providers/${contractorId}`);
    setC(data);
    setDraft({
      name: data.name || "",
      address: data.address || "",
      phones: (data.phones && data.phones.length)
        ? data.phones.map((p) => ({ id: p.id || crypto.randomUUID(), ...p }))
        : [{ id: crypto.randomUUID(), label: "Mobile", number: "" }],
      household_members: data.household_members || [],
    });
    const cls = await api.get("/clients");
    setClientsAssigned(cls.data.filter((cl) => cl.contractor_id === contractorId));
  };
  useEffect(() => { load(); }, [contractorId]);

  if (!c || !draft) return <p className="text-muted-foreground">Loading…</p>;

  const updatePhone = (idx, patch) => setDraft((d) => ({ ...d, phones: d.phones.map((p, i) => i === idx ? { ...p, ...patch } : p) }));
  const removePhone = (idx) => setDraft((d) => ({ ...d, phones: d.phones.filter((_, i) => i !== idx) }));
  const addPhone = () => setDraft((d) => ({ ...d, phones: [...d.phones, { id: crypto.randomUUID(), label: "Home", number: "" }] }));

  const updateMember = (idx, patch) => setDraft((d) => ({ ...d, household_members: d.household_members.map((m, i) => i === idx ? { ...m, ...patch } : m) }));
  const removeMember = (idx) => setDraft((d) => ({ ...d, household_members: d.household_members.filter((_, i) => i !== idx) }));
  const addMember = () => setDraft((d) => ({
    ...d,
    household_members: [...d.household_members, { id: crypto.randomUUID(), name: "", relation: "", dob: "" }],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const cleanPhones = (draft.phones || []).filter((p) => (p.number || "").trim());
      const cleanMembers = (draft.household_members || []).filter((m) => (m.name || "").trim());
      const { data } = await api.put(`/users/providers/${contractorId}`, {
        name: draft.name,
        address: draft.address,
        phones: cleanPhones,
        household_members: cleanMembers,
      });
      setC(data);
      toast.success("Contractor profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this contractor's access? They will be immediately signed out and unable to log back in.")) return;
    await api.post(`/users/providers/${c.id}/revoke`);
    toast.success("Access revoked. Active sessions terminated.");
    load();
  };
  const reinstate = async () => {
    await api.post(`/users/providers/${c.id}/reinstate`);
    toast.success("Access reinstated");
    load();
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-contractor-profile">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/profiles")} data-testid="back-to-profiles">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button onClick={save} disabled={saving} data-testid="save-contractor">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      <Card className={`p-6 ${c.revoked ? "bg-red-50/40 border-red-200" : ""}`} data-testid="contractor-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-label">Contractor Profile</p>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="font-display font-bold text-2xl border-0 px-0 h-auto focus-visible:ring-0 shadow-none"
              data-testid="contractor-name-input"
            />
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
              <Mail className="w-4 h-4" /> {c.email}
            </div>
            <div className="mt-2">
              {c.revoked
                ? <Badge variant="destructive">Access Revoked</Badge>
                : <Badge className="bg-emerald-600 hover:bg-emerald-600">Active</Badge>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {c.revoked ? (
              <Button variant="outline" onClick={reinstate} data-testid="profile-reinstate">
                <ShieldCheck className="w-4 h-4 mr-2 text-emerald-600" /> Reinstate access
              </Button>
            ) : (
              <Button variant="outline" onClick={revoke} className="text-red-600 hover:text-red-700" data-testid="profile-revoke">
                <ShieldOff className="w-4 h-4 mr-2" /> Revoke contractor access
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-primary" />
          <p className="text-label">Full Address</p>
        </div>
        <Input
          placeholder="Street, City, State, ZIP"
          value={draft.address}
          onChange={(e) => setDraft({ ...draft, address: e.target.value })}
          data-testid="contractor-address-input"
        />
      </Card>

      <Card className="p-5 space-y-3" data-testid="contractor-phones">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-primary" />
            <p className="text-label">Phone Numbers</p>
          </div>
          <Button onClick={addPhone} size="sm" variant="outline" data-testid="add-phone"><Plus className="w-3.5 h-3.5 mr-1" /> Add phone</Button>
        </div>
        {draft.phones.length === 0 && <p className="text-xs text-muted-foreground">No phones on file.</p>}
        {draft.phones.map((p, idx) => (
          <div key={p.id || idx} className="grid grid-cols-[140px_1fr_auto] gap-2 items-center" data-testid={`phone-${idx}`}>
            <Input value={p.label || ""} onChange={(e) => updatePhone(idx, { label: e.target.value })} placeholder="Label (Mobile, Home...)" />
            <Input value={p.number || ""} onChange={(e) => updatePhone(idx, { number: e.target.value })} placeholder="(555) 123-4567" />
            <Button variant="ghost" size="sm" onClick={() => removePhone(idx)}>
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        ))}
      </Card>

      <Card className="p-5 space-y-3" data-testid="contractor-household">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <p className="text-label">Household Members</p>
          </div>
          <Button onClick={addMember} size="sm" variant="outline" data-testid="add-member"><Plus className="w-3.5 h-3.5 mr-1" /> Add member</Button>
        </div>
        {draft.household_members.length === 0 && <p className="text-xs text-muted-foreground">No household members yet.</p>}
        {draft.household_members.map((m, idx) => {
          const age = computeAge(m.dob);
          return (
            <div key={m.id || idx} className="grid grid-cols-[1fr_1fr_140px_70px_auto] gap-2 items-center" data-testid={`member-${idx}`}>
              <Input placeholder="Name" value={m.name || ""} onChange={(e) => updateMember(idx, { name: e.target.value })} />
              <Input placeholder="Relation" value={m.relation || ""} onChange={(e) => updateMember(idx, { relation: e.target.value })} />
              <Input type="date" value={m.dob || ""} onChange={(e) => updateMember(idx, { dob: e.target.value })} title="Date of Birth" />
              <div className="text-sm text-muted-foreground text-center" data-testid={`member-age-${idx}`}>
                {age != null ? `${age} yrs` : "—"}
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeMember(idx)}>
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          );
        })}
        <p className="text-[11px] text-muted-foreground pt-1">Age is computed automatically from Date of Birth.</p>
      </Card>

      <Card className="p-5">
        <p className="text-label mb-2">Assigned Clients</p>
        {clientsAssigned.length === 0 && <p className="text-sm text-muted-foreground">No assigned clients.</p>}
        <ul className="space-y-1">
          {clientsAssigned.map((cl) => (
            <li key={cl.id} className="text-sm flex items-center justify-between border-b border-border last:border-0 py-1.5">
              <span>{cl.name}</span>
              <Button variant="link" size="sm" onClick={() => navigate(`/admin/profiles/client/${cl.id}`)}>View profile</Button>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="p-5 border-l-4 border-l-red-500 bg-red-50/30">
        <p className="text-label text-red-700">Security note</p>
        <p className="text-sm mt-1">
          Revoking access immediately invalidates this contractor's session tokens. They will be
          forcibly signed out across all devices and cannot log back in until reinstated.
        </p>
      </Card>
    </div>
  );
}
